import { useCallback } from 'react';
import { overlay } from 'overlay-kit';

import { type QuizGenerateItem } from '@/api/ai/type';
import { useGenerationModeUiCohort } from '@/api/ai/useGenerationModeUiCohort';
import { quotaExceededModal } from '@/shared/features/ai-quiz-generation/components';
import { useAiTrialQuotaQuery } from '@/shared/features/ai-quiz-generation/hooks';
import { type AttachmentState } from '@/shared/features/ai-quiz-generation/types';
import { adaptAiGeneratedQuizzesToBuildForm } from '@/views/build/features/ai-quiz-generation/utils';

import { MAX_QUIZ_COUNT } from '../constant';
import { useBuildInfoContext } from '../context/BuildInfoProvider';
import { useBuildFormContext } from '../form/schema';
import { useBuildQuestionAlert } from '../hook/useBuildQuestionAlert';
import { buildAiModal, buildAiModalOverlayId } from '../modal/BuildAiModal';
import { aiSaveAnimation } from '../util/aiSaveAnimation';

/**
 * categoryId에서 categoryGroupId 추출
 * categoryId는 7자리 (학년 5자 + 과목 2자) 형식
 * 마지막 2자리만 추출하여 과목 그룹 ID를 반환
 *
 * @param categoryId - 7자리 카테고리 ID (예: "1010106")
 * @returns categoryGroupId - 2자리 과목 그룹 ID (예: "06")
 *
 * @example
 * extractCategoryGroupId("1010106") // "06"
 */
function extractCategoryGroupId(categoryId: string): string {
  return categoryId.slice(5);
}

export const useOpenBuildAiModal = () => {
  const { subjectTree, schoolInfoTree } = useBuildInfoContext();
  const { getValues, setValue } = useBuildFormContext();
  const { openQuestionCountLimitAlert, openQuestionSaveSuccessAlert } =
    useBuildQuestionAlert();
  const { isQuotaExhausted } = useAiTrialQuotaQuery();
  const uiCohort = useGenerationModeUiCohort();

  const handleSaveAiGeneratedQuizzes = useCallback(
    (quizzes: QuizGenerateItem[], generationId: string): boolean => {
      if (quizzes.length + getValues('quizList').length > MAX_QUIZ_COUNT) {
        openQuestionCountLimitAlert();
        return false;
      }

      const newQuizList = adaptAiGeneratedQuizzesToBuildForm(
        quizzes,
        generationId,
      );
      setValue('quizList', [...getValues('quizList'), ...newQuizList]);
      aiSaveAnimation(newQuizList.map(quiz => quiz.id));

      // 저장 후 최대 문제 수에 도달했는지 확인
      const totalCount = getValues('quizList').length;
      if (totalCount >= MAX_QUIZ_COUNT) {
        // 최대치 도달 시 모달 닫기
        overlay.unmount(buildAiModalOverlayId);
      }
      openQuestionSaveSuccessAlert();
      return true;
    },
    [
      getValues,
      setValue,
      openQuestionCountLimitAlert,
      openQuestionSaveSuccessAlert,
    ],
  );

  return useCallback(
    (type: AttachmentState['type'] | 'floating') => {
      // Quota guard - check if quota is exhausted
      if (isQuotaExhausted) {
        quotaExceededModal.open();
        return;
      }

      const quizList = getValues('quizList');
      if (quizList.length >= MAX_QUIZ_COUNT) {
        openQuestionCountLimitAlert();
        return;
      }

      // BuildAiCategoryProvider에 전달할 값들
      const subject = getValues('subject');
      const categoryProviderValue = {
        schoolInfoTree,
        subjectTree,
        initialSchoolInfoId: getValues('gradeId'), // gradeId contains full school+grade info
        initialSubjectId: subject ? extractCategoryGroupId(subject) : undefined,
      };

      buildAiModal.open({
        onSave: handleSaveAiGeneratedQuizzes,
        quizCountLimit: MAX_QUIZ_COUNT - getValues('quizList').length,
        type,
        // features는 생략 - DEFAULT_FEATURES 사용 (achievements: true, autoTrigger: false)
        triggerSource: 'manual',
        uiCohort,
        categoryProviderValue,
      });
    },
    [
      isQuotaExhausted,
      getValues,
      schoolInfoTree,
      openQuestionCountLimitAlert,
      handleSaveAiGeneratedQuizzes,
      subjectTree,
      uiCohort,
    ],
  );
};
